/**
 * DofPrintPage.jsx — Bölüm 26-C
 * Uygunsuzluk Yazdırma Sayfası
 *
 * Açık Uygunsuzluk  (inspectionLevelId=2, isDone=false) → "Düzeltilmiş Durum" bölümü boş gösterilir
 * Kapalı Uygunsuzluk (inspectionLevelId=3, isDone=true) → Her iki fotoğraf + kapanış tarihi/notu gösterilir
 *
 * Veri akışı:
 *   1. getFormDetail(formId)              → meta (isDone, fuarId, firmaName, user*, parentFormId) + kendi içerik satırı
 *   2. getForm(formId)                    → parentFormId (ham)
 *   3. getChildForms(parentFormId)        → bu formun sourceQuestionId / sourceHazardId / sourceNonconformityId'si (#600)
 *   4. getFormDetail(parentFormId)        → tetikleyen sorunun satırı (sourceQuestionId ile eşlenir) → ad alanları
 *   5. getHazardTree(hazardId)            → olasilik/frekans/siddet + legalProcedures + suggestions
 *   6. getPhotos(formId, {levelId: 2|3})  → Riskli durum / düzeltilmiş durum fotoğrafları;
 *      Kontrol formunda çekilen "önce" fotoğrafları üst formda o sorunun altındadır → getPhotos(parent, {questionId})
 *
 * Puan yalnız uygunsuzluk (nonconformity) kimliği bilinen ya da ağaçta tek olan kayıtlar için gösterilir;
 * eşleşme yoksa "—" (eskiden ağacın ilk kaydı yazılıyordu → yanlış puan).
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { Printer, X } from "lucide-react";
import axios from "axios";
import {
  getFormDetail,
  getForm,
  getAnswers,
  getChildForms,
  getPhotos,
  getPhotoUrl,
  getHazardTree,
} from "../../services/sahaInspectionService";
import { getAuthHeader } from "../../Components/Contexts/BackendConstants";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

/**
 * Auth header gerektiren fotoğraf URL'ini blob URL'e dönüştürür.
 * <img> tag'ı auth header gönderemediğinden axios ile çeker.
 */
async function fetchPhotoBlob(url) {
  if (!url) return null;
  try {
    const res = await axios.get(url, {
      headers: getAuthHeader(),
      responseType: "blob",
    });
    return URL.createObjectURL(res.data);
  } catch {
    return url; // hata olursa direkt URL ile dene
  }
}

async function resolvePhotos(photoList) {
  return Promise.all(
    photoList.map(async (p) => {
      const rawUrl = getPhotoUrl(p);
      const blobUrl = await fetchPhotoBlob(rawUrl);
      return { ...p, _resolvedUrl: blobUrl };
    })
  );
}

/** Denetmen adını API'dan gelen farklı alan isimlerine karşı güvenli okur */
function resolveUserName(detail) {
  if (!detail) return "—";
  const first =
    detail.userName      ||
    detail.userFirstName ||
    detail.firstName     ||
    detail.name          ||
    "";
  const last =
    detail.userSurname   ||
    detail.userLastName  ||
    detail.lastName      ||
    detail.surname       ||
    "";
  const full = `${first} ${last}`.trim();
  return full || (detail.userId ? `#${detail.userId}` : "—");
}

// ── Risk renk / etiket yardımcıları ─────────────────────────────────────────
function riskHexColor(score) {
  if (!score) return "#aaa";
  if (score < 20)  return "#28a745";  // yeşil  — önemsiz
  if (score < 70)  return "#1a6fb5";  // mavi   — olası
  if (score < 200) return "#d4a017";  // sarı   — önemli
  if (score < 400) return "#e07b00";  // turuncu— esaslı
  return "#c82333";                   // kırmızı— kabul edilemez
}
function riskLabelText(score) {
  if (!score) return "—";
  if (score < 20)  return i18n.t("saha:dofPrint.onemsizRisk");
  if (score < 70)  return i18n.t("saha:dofPrint.olasiRisk");
  if (score < 200) return i18n.t("saha:dofPrint.onemliRisk");
  if (score < 400) return i18n.t("saha:dofPrint.esasliRisk");
  return i18n.t("saha:dofPrint.kabulEdilemezRisk");
}
const RISK_SCALE = [
  { get label() { return i18n.t("saha:dofPrint.kabulEdilemezRisk"); }, range: "400 < R",        color: "#c82333" },
  { get label() { return i18n.t("saha:dofPrint.esasliRisk"); },         range: "200 < R < 400",  color: "#e07b00" },
  { get label() { return i18n.t("saha:dofPrint.onemliRisk"); },         range: "70 < R < 200",   color: "#d4a017" },
  { get label() { return i18n.t("saha:dofPrint.olasiRisk"); },          range: "20 < R < 70",    color: "#1a6fb5" },
  { get label() { return i18n.t("saha:dofPrint.onemsizRisk"); },        range: "R < 20",         color: "#28a745" },
];

// ── Tarih / Saat formatlayıcılar ─────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(currentLocaleTag(), {
    hour: "2-digit", minute: "2-digit",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
export default function DofPrintPage() {
  const { t } = useTranslation("saha");
  const { formId } = useParams();

  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [detail,       setDetail]       = useState(null);   // DÖF form meta
  const [contentRow,   setContentRow]   = useState(null);   // adların (kategori/soru/uygunsuzluk/tehlike) geldiği içerik satırı
  const [photosNC,     setPhotosNC]     = useState([]);     // level 2 — riskli durum
  const [photosDOF,    setPhotosDOF]    = useState([]);     // level 3 — düzeltilmiş durum
  const [hazardTree,   setHazardTree]   = useState(null);
  const [sourceNcId,   setSourceNcId]   = useState(null);   // eşleşme için NC id

  useEffect(() => {
    document.title = i18n.t("saha:dofPrint.uygunsuzlukRaporu");

    async function load() {
      if (!formId) {
        setError(i18n.t("saha:dofPrint.formIdBulunamadi"));
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // ── 1. DÖF formu + cevaplar + fotoğraflar paralel ──────────────────
        const [det, rawForm, ans, p2own, p3raw] = await Promise.all([
          getFormDetail(formId),
          getForm(formId),
          getAnswers(formId),
          getPhotos(formId, { inspectionLevelId: 2 }),
          getPhotos(formId, { inspectionLevelId: 3 }),
        ]);
        setDetail(det);

        // ── 2. Bu formun kendi içerik satırı (en yeni, uygunsuzluk kimlikli) ──
        const latest = (rows) => [...(rows || [])].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        const ownRow = latest(det?.contents).find((c) => c.hazardId) || latest(det?.contents)[0] || null;
        const parentId = rawForm?.parentFormId ?? det?.parentFormId ?? null;

        // ── 3. Üst form: bu formun tetikleyen sorusu (/children → sourceQuestionId, #600) ve o sorunun satırı ──
        let link = null;          // /children içindeki bu form
        let parentRow = null;     // üst formda tetikleyen sorunun içerik satırı
        let p2parent = [];        // Kontrol formunda o soruya çekilen "önce" fotoğrafları (üst formda durur)
        if (parentId) {
          const [pdet, kids] = await Promise.all([getFormDetail(parentId), getChildForms(parentId)]);
          link = (kids || []).find((k) => String(k.id || k._id) === String(formId)) || null;
          const srcQ = link?.sourceQuestionId ?? rawForm?.sourceQuestionId ?? ownRow?.questionId ?? null;
          const parentRows = latest(pdet?.contents);
          if (srcQ != null) {
            parentRow = parentRows.find((c) => c.questionId === srcQ) || null;
            const pp = await getPhotos(parentId, { questionId: srcQ });
            p2parent = (Array.isArray(pp) ? pp : []).filter((p) => p.inspectionLevelId === 2);
          } else {
            // soru bilinmiyor: üst formda tek "Uygun Değil" satırı varsa o, yoksa boş (rastgele cevap yazılmaz)
            const bad = parentRows.filter((c) => c.answerValue === 2);
            parentRow = bad.length === 1 ? bad[0] : null;
          }
        }

        // ── 4. Adların geldiği satır ve puan için uygunsuzluk kimlikleri ──
        const row = ownRow?.hazardId ? ownRow : (parentRow || ownRow);
        setContentRow(row);
        const firstAns = Object.values(ans || {})[0];
        const hazardIdToUse = row?.hazardId ?? link?.sourceHazardId ?? rawForm?.sourceHazardId ?? firstAns?.hazardId ?? null;
        const ncIdToUse     = row?.nonconformityId ?? link?.sourceNonconformityId ?? rawForm?.sourceNonconformityId ?? firstAns?.nonconformityId ?? null;
        setSourceNcId(ncIdToUse);

        // ── 5. Fotoğrafları auth header ile çekip blob URL'e dönüştür ──────
        const seen = new Set();
        const p2raw = [...(Array.isArray(p2own) ? p2own : []), ...p2parent].filter((p) => !seen.has(p.id) && seen.add(p.id));
        const [p2, p3] = await Promise.all([
          resolvePhotos(p2raw),
          resolvePhotos(Array.isArray(p3raw) ? p3raw : []),
        ]);
        setPhotosNC(p2);
        setPhotosDOF(p3);

        // ── 5. HazardTree → olasılık / frekans / şiddet + öneriler + mevzuat ─
        if (hazardIdToUse) {
          const tree = await getHazardTree(hazardIdToUse);
          setHazardTree(tree);
        }
      } catch (e) {
        console.error("[DofPrintPage] veri yükleme hatası:", e);
        setError(i18n.t("saha:dofPrint.verilerYuklenirkenHataOlustu"));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [formId]);

  // ── Yükleniyor / Hata ────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:"100vh" }}>
      <Spinner /> <span style={{ marginLeft:10 }}>{t("dofPrint.yukleniyor")}</span>
    </div>
  );
  if (error || !detail) return (
    <div style={{ textAlign:"center", padding:40, color:"#c82333" }}>
      {error || t("dofPrint.formBulunamadi")}
    </div>
  );

  // ── Türetilmiş değerler ──────────────────────────────────────────────────
  // İsim alanları: formun kendi içerik satırı, yoksa üst formda tetikleyen sorunun satırı
  const content0 = contentRow || {};
  const isDone   = !!detail.isDone;

  // Sıra / Form numarası — API hangi alanı dönerse kullanalım
  const formNo =
    detail.formNo           ??
    detail.sequenceNumber   ??
    detail.inspectionFormNo ??
    detail.no               ??
    (formId || "").slice(-10).toUpperCase();

  // getHazardTree içinden eşleşen nonconformity'yi bul → olasilik/frekans/siddet
  let ncDetail = null;
  if (hazardTree && sourceNcId) {
    outer: for (const r of (hazardTree.risks || [])) {
      for (const nc of (r.nonconformities || [])) {
        if (String(nc.nonconformityId) === String(sourceNcId)) {
          ncDetail = nc;
          break outer;
        }
      }
    }
  }
  // Kimlik bilinmiyorsa yalnız ağaçta tek uygunsuzluk varsa o gösterilir; aksi halde "—" (yanlış puan yazılmaz)
  if (hazardTree && !ncDetail && !sourceNcId) {
    const all = (hazardTree.risks || []).flatMap((r) => r.nonconformities || []);
    if (all.length === 1) ncDetail = all[0];
  }

  const riskScore    = ncDetail?.riskDegeri ?? null;
  const legalList    = hazardTree?.legalProcedures || [];
  const suggList     = hazardTree?.suggestions || [];
  const oneriText    = ncDetail?.oneri || "";

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="dof-wrapper">

      {/* Aksiyon butonları — sadece ekranda görünür, baskıda gizlenir */}
      <div className="dof-actions no-print">
        <button className="dof-btn-primary" onClick={() => window.print()}>
          <Printer size={14} style={{ marginRight:6 }} />{" "}{t("dofPrint.yazdirPdfAl")}
        </button>
        <button className="dof-btn-secondary" onClick={() => window.close()}>
          <X size={14} style={{ marginRight:6 }} />{" "}{t("common:kapat")}
        </button>
        {!isDone && (
          <span className="dof-open-badge">{t("dofPrint.acikUygunsuzlukHenuzKapatilmamis")}</span>
        )}
        {isDone && (
          <span className="dof-closed-badge">{t("dofPrint.kapaliUygunsuzluk")}</span>
        )}
      </div>

      {/* Baskı alanı */}
      <div className="dof-page">

        {/* ── BAŞLIK ──────────────────────────────────────────────────────── */}
        <div className="dof-header">
          <div className="dof-logo-left">
            <img src="/logo192.png" alt={t("dofPrint.logo")} style={{ height:50 }} />
          </div>
          <div className="dof-header-center">
            <div className="dof-main-title">
              {detail.fuarId ? t("dofPrint.fuar", { fuarId: detail.fuarId }) : ""}
              {detail.firmaName || t("dofPrint.hedef", { v: detail.scopeTargetId || "—" })}
            </div>
            <div className="dof-sub-title">
              {isDone ? t("dofPrint.kapaliUygunsuzlukRaporu") : t("dofPrint.acikUygunsuzlukRaporu")}
            </div>
          </div>
          <div className="dof-logo-right">
            <img src="/tr.png" alt={t("dofPrint.logo")} style={{ height:50 }} onError={(e) => { e.target.style.display="none"; }} />
          </div>
        </div>

        {/* ── META BİLGİLER ────────────────────────────────────────────────── */}
        <table className="dof-table">
          <tbody>
            <tr>
              <td className="dof-th">{t("dofPrint.formNo")}</td>
              <td className="dof-td">{formNo}</td>
              <td className="dof-th">{t("dofPrint.tarih")}</td>
              <td className="dof-td">{fmtDate(detail.createdAt)}</td>
              <td className="dof-th">{t("dofPrint.saat")}</td>
              <td className="dof-td">{fmtTime(detail.createdAt)}</td>
            </tr>
            <tr>
              <td className="dof-th">{t("dofPrint.denetmen")}</td>
              <td className="dof-td" colSpan={3}>
                {resolveUserName(detail)}
              </td>
              <td className="dof-th">{t("dofPrint.hedef2")}</td>
              <td className="dof-td">{detail.scopeTargetName || detail.scopeTargetId || "—"}</td>
            </tr>
            {isDone && (
              <tr>
                <td className="dof-th">{t("dofPrint.kapanis")}</td>
                <td className="dof-td" colSpan={5}>
                  {fmtDate(detail.completedAt)} {fmtTime(detail.completedAt)}
                  {detail.notes ? ` — ${detail.notes}` : ""}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* ── RİSK ANALİZİ ─────────────────────────────────────────────────── */}
        <div className="dof-section-head">{t("dofPrint.riskAnalizi")}</div>
        <table className="dof-table">
          <tbody>
            <tr>
              <td className="dof-th" style={{ width:160 }}>{t("dofPrint.faaliyet")}</td>
              <td className="dof-td">{content0.categoryName || "—"}</td>
              <td className="dof-th" style={{ width:130 }}>{t("dofPrint.sorumluFirma")}</td>
              <td className="dof-td">{detail.firmaName || "—"}</td>
            </tr>
            <tr>
              <td className="dof-th">{t("dofPrint.konu")}</td>
              <td className="dof-td" colSpan={3}>{content0.questionName || "—"}</td>
            </tr>
            <tr>
              <td className="dof-th">{t("dofPrint.tehlikeUygunsuzluk")}</td>
              <td className="dof-td" colSpan={3}>{content0.hazardName || "—"}</td>
            </tr>
            <tr>
              <td className="dof-th">{t("dofPrint.riskTehlike")}</td>
              <td className="dof-td" colSpan={3}>{content0.riskName || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* Risk Puanlama */}
        <table className="dof-table" style={{ marginTop:1 }}>
          <thead>
            <tr>
              <th className="dof-score-head">{t("dofPrint.olasilik")}</th>
              <th className="dof-score-head">{t("dofPrint.frekans")}</th>
              <th className="dof-score-head">{t("dofPrint.siddet")}</th>
              <th className="dof-score-head">{t("dofPrint.riskDegeri")}</th>
              <th className="dof-score-head">{t("dofPrint.mevcutRiskSinifi")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="dof-score-cell">{ncDetail?.olasilik ?? "—"}</td>
              <td className="dof-score-cell">{ncDetail?.frekans  ?? "—"}</td>
              <td className="dof-score-cell">{ncDetail?.siddet   ?? "—"}</td>
              <td className="dof-score-cell" style={{ fontWeight:"bold", color: riskHexColor(riskScore) }}>
                {riskScore ?? "—"}
              </td>
              <td className="dof-score-cell">
                {riskScore != null ? (
                  <span style={{
                    background: riskHexColor(riskScore),
                    color: "#fff",
                    padding: "2px 12px",
                    borderRadius: 4,
                    fontWeight: "bold",
                    fontSize: 11,
                  }}>
                    {riskLabelText(riskScore)}
                  </span>
                ) : "—"}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── DURUM TESPİT FOTOĞRAFLARI ────────────────────────────────────── */}
        <div className="dof-section-head">{t("dofPrint.durumTespitFotograflari")}</div>
        <div className="dof-photos-row">

          {/* Sol: Riskli Durum */}
          <div className="dof-photo-col">
            <div className="dof-photo-label">{t("dofPrint.riskliDurumOnce")}</div>
            {photosNC.length > 0 ? (
              <div className="dof-photo-grid">
                {photosNC.slice(0, 4).map((p, i) => {
                  const url = p._resolvedUrl || getPhotoUrl(p);
                  return url
                    ? <img key={i} src={url} alt={t("dofPrint.riskli")} className="dof-photo-img" />
                    : <div key={i} className="dof-photo-placeholder">{t("dofPrint.gorselYok")}</div>;
                })}
              </div>
            ) : (
              <div className="dof-photo-empty">{t("dofPrint.fotografEklenmemis")}</div>
            )}
          </div>

          {/* Sağ: Düzeltilmiş Durum */}
          <div className="dof-photo-col">
            <div className="dof-photo-label">
              {isDone ? t("dofPrint.duzeltilmisDurumSonra") : t("dofPrint.uygunsuzlukAcikHenuzKapatilmadi")}
            </div>
            {isDone && photosDOF.length > 0 ? (
              <div className="dof-photo-grid">
                {photosDOF.slice(0, 4).map((p, i) => {
                  const url = p._resolvedUrl || getPhotoUrl(p);
                  return url
                    ? <img key={i} src={url} alt={t("dofPrint.duzeltilmis")} className="dof-photo-img" />
                    : <div key={i} className="dof-photo-placeholder">{t("dofPrint.gorselYok")}</div>;
                })}
              </div>
            ) : isDone ? (
              <div className="dof-photo-empty">{t("dofPrint.kapanisFotografiEklenmemis")}</div>
            ) : (
              <div className="dof-photo-empty dof-open">
                {t("dofPrint.buUygunsuzlukHenuzKapatilmamistir")}<br />
                {t("dofPrint.duzeltilmisDurumFotografiMevcutDegil")}
              </div>
            )}
          </div>
        </div>

        {/* ── DEĞERLENDİRME VE ÖNERİLER ──────────────────────────────────── */}
        <div className="dof-section-head">{t("dofPrint.degerlendirmeVeOneriler")}</div>
        <table className="dof-table">
          <tbody>
            <tr>
              <td className="dof-th" style={{ width:200 }}>{t("dofPrint.uygunsuzluk")}</td>
              <td className="dof-td">{hazardTree?.hazardName || content0.hazardName || "—"}</td>
            </tr>
            {/* 4.2 — Hazard altındaki risk ağacı */}
            {hazardTree?.risks?.length > 0 && (
              <tr>
                <td className="dof-th" style={{ verticalAlign: "top" }}>{t("dofPrint.tehlikeRiskDetayi")}</td>
                <td className="dof-td">
                  {hazardTree.risks.map((r, ri) => (
                    <div key={ri} style={{ marginBottom: 6 }}>
                      <div style={{ fontWeight: "bold" }}>
                        {ri + 1}. {r.riskName}
                      </div>
                      {(r.nonconformities || []).map((nc, ni) => (
                        <div key={ni} style={{ paddingLeft: 12, fontSize: 11, color: "#555" }}>
                          • {nc.nonconformityName}
                          {nc.riskDegeri != null && (
                            <span style={{
                              marginLeft: 6,
                              background: riskHexColor(nc.riskDegeri),
                              color: "#fff",
                              padding: "1px 6px",
                              borderRadius: 3,
                              fontSize: 10,
                            }}>{nc.riskDegeri}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </td>
              </tr>
            )}
            <tr>
              <td className="dof-th">{t("dofPrint.duzelticiOnleyiciOneriler")}</td>
              <td className="dof-td">
                {suggList.length > 0
                  ? suggList.map((s, i) => (
                      <div key={i}>{s.translated_name || s.inspection_suggestion_name || "—"}</div>
                    ))
                  : oneriText || "—"}
              </td>
            </tr>
            <tr>
              <td className="dof-th">{t("dofPrint.yasalZorunlulukDayanak")}</td>
              <td className="dof-td">
                {legalList.length > 0
                  ? legalList.map((lp, i) => (
                      <div key={i}>{lp.translated_name || lp.inspection_legal_procedure_name || "—"}</div>
                    ))
                  : "—"}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── RİSK DEĞERİ İNDEKSİ (sabit) ───────────────────────────────── */}
        <div className="dof-section-head">{t("dofPrint.riskDegeriIndeksi")}</div>
        <div className="dof-scale">
          {RISK_SCALE.map((r) => (
            <div key={r.label} className="dof-scale-item">
              <div className="dof-scale-dot" style={{ background: r.color }} />
              <div className="dof-scale-text">
                <strong>{r.label}</strong><br />
                <small style={{ color:"#666" }}>{r.range}</small>
              </div>
            </div>
          ))}
        </div>

      </div>{/* /dof-page */}

      {/* ── CSS ─────────────────────────────────────────────────────────── */}
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; background: #f4f4f4; }

        .dof-wrapper {
          font-family: Arial, sans-serif;
          font-size: 12px;
          max-width: 960px;
          margin: 0 auto;
          padding: 16px;
          background: #f4f4f4;
          min-height: 100vh;
        }

        /* Aksiyon çubuğu */
        .dof-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 14px;
          padding: 10px 14px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 4px rgba(0,0,0,.1);
        }
        .dof-btn-primary {
          display: flex; align-items: center;
          background: #0d6efd; color: #fff; border: none;
          padding: 6px 14px; border-radius: 5px; font-size: 13px; cursor: pointer;
        }
        .dof-btn-primary:hover { background: #0b5ed7; }
        .dof-btn-secondary {
          display: flex; align-items: center;
          background: #6c757d; color: #fff; border: none;
          padding: 6px 14px; border-radius: 5px; font-size: 13px; cursor: pointer;
        }
        .dof-btn-secondary:hover { background: #5a6268; }
        .dof-open-badge {
          background: #fff3cd; color: #856404;
          padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: bold;
        }
        .dof-closed-badge {
          background: #d1eddc; color: #155724;
          padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: bold;
        }

        /* Baskı sayfası */
        .dof-page {
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 20px 24px;
        }

        /* Başlık */
        .dof-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 2px solid #222;
          padding-bottom: 10px;
          margin-bottom: 10px;
        }
        .dof-header-center { text-align: center; flex: 1; }
        .dof-main-title { font-size: 14px; font-weight: bold; }
        .dof-sub-title { font-size: 11px; color: #555; letter-spacing: 1px; margin-top: 2px; }

        /* Bölüm başlığı */
        .dof-section-head {
          background: #2c2c2c;
          color: #fff;
          padding: 4px 10px;
          font-size: 11px;
          font-weight: bold;
          letter-spacing: 0.5px;
          margin: 8px 0 0;
        }

        /* Genel tablo */
        .dof-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 0;
        }
        .dof-table td { border: 1px solid #bbb; padding: 5px 8px; vertical-align: top; }
        .dof-th {
          background: #ebebeb;
          font-weight: bold;
          font-size: 11px;
          white-space: nowrap;
        }
        .dof-td { font-size: 12px; }

        /* Puan tablosu */
        .dof-score-head {
          background: #2c2c2c;
          color: #fff;
          font-size: 11px;
          padding: 5px 8px;
          text-align: center;
          border: 1px solid #bbb;
        }
        .dof-score-cell {
          text-align: center;
          padding: 5px 8px;
          border: 1px solid #bbb;
          font-size: 12px;
        }

        /* Fotoğraflar */
        .dof-photos-row {
          display: flex;
          gap: 8px;
          margin-bottom: 0;
          border: 1px solid #bbb;
          border-top: none;
        }
        .dof-photo-col {
          flex: 1;
          padding: 8px;
          border-right: 1px solid #bbb;
          min-height: 160px;
        }
        .dof-photo-col:last-child { border-right: none; }
        .dof-photo-label {
          font-weight: bold;
          font-size: 11px;
          padding-bottom: 6px;
          border-bottom: 1px solid #e0e0e0;
          margin-bottom: 8px;
        }
        .dof-photo-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .dof-photo-img {
          width: 130px;
          height: 110px;
          object-fit: cover;
          border: 1px solid #ccc;
          border-radius: 3px;
        }
        .dof-photo-placeholder {
          width: 130px; height: 110px;
          border: 1px dashed #ccc;
          display: flex; align-items: center; justify-content: center;
          color: #999; font-size: 10px;
        }
        .dof-photo-empty {
          color: #999;
          font-size: 11px;
          text-align: center;
          padding: 24px 10px;
        }
        .dof-photo-empty.dof-open {
          background: #fff8e1;
          color: #c46200;
          border: 1px dashed #e09000;
          border-radius: 4px;
          margin: 8px 0;
        }

        /* Risk skalası */
        .dof-scale {
          display: flex;
          gap: 4px;
          border: 1px solid #bbb;
          border-top: none;
          padding: 10px 8px;
        }
        .dof-scale-item {
          flex: 1;
          display: flex;
          align-items: flex-start;
          gap: 7px;
        }
        .dof-scale-dot {
          width: 18px;
          height: 46px;
          border-radius: 3px;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .dof-scale-text { font-size: 10px; line-height: 1.5; }

        /* Baskı medya kuralları */
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .dof-wrapper { padding: 0; max-width: 100%; background: #fff; }
          .dof-page { border: none; padding: 8px; box-shadow: none; }
          .dof-photo-img { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

